import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Header from './components/Header';
import PuzzleList from './components/PuzzleList';
import CrosswordPlayer from './components/CrosswordPlayer';
import CrosswordBuilder from './components/CrosswordBuilder';
import Archive from './components/Archive';
import UserStats from './components/UserStats';
import Leaderboard from './components/Leaderboard';
import AuthModal from './components/AuthModal';
import { authAPI, puzzleAPI } from './api';
import { Puzzle, User } from './types';
import { colors, fonts } from './theme';

const AppContainer = styled.div`
  min-height: 100vh;
  background: ${colors.paper};
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  padding-bottom: 56px;
`;

const StatusMessage = styled.div`
  text-align: center;
  padding: 64px 20px;
  font-family: ${fonts.display};
  font-size: 17px;
  color: ${colors.inkFaint};
`;

const PuzzlePage: React.FC<{ currentUser: User | null }> = ({ currentUser }) => {
  const { id } = useParams<{ id: string }>();
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPuzzle = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const puzzleData = await puzzleAPI.getPuzzle(parseInt(id, 10));
        setPuzzle(puzzleData);
      } catch (error) {
        console.error('Error loading puzzle:', error);
        setPuzzle(null);
      } finally {
        setLoading(false);
      }
    };

    loadPuzzle();
  }, [id]);

  if (loading) {
    return <StatusMessage>Loading puzzle…</StatusMessage>;
  }

  if (!puzzle) {
    return <StatusMessage>That puzzle couldn’t be found.</StatusMessage>;
  }

  // Remount on id change so a fresh puzzle resets the grid and timer.
  return <CrosswordPlayer key={puzzle.id} puzzle={puzzle} currentUser={currentUser} />;
};

function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    authAPI
      .getCurrentUser()
      .then(setCurrentUser)
      .catch(() => {
        localStorage.removeItem('token');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleAuthSuccess = useCallback(async () => {
    try {
      const user = await authAPI.getCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      console.error('Error getting user after auth:', error);
    }
  }, []);

  const handleSignOut = useCallback(() => {
    localStorage.removeItem('token');
    setCurrentUser(null);
  }, []);

  if (loading) {
    return (
      <AppContainer>
        <StatusMessage>Loading…</StatusMessage>
      </AppContainer>
    );
  }

  return (
    <Router>
      <AppContainer>
        <Header
          currentUser={currentUser}
          onSignIn={() => setShowAuthModal(true)}
          onSignOut={handleSignOut}
        />

        <MainContent>
          <Routes>
            <Route path="/" element={<PuzzleList />} />
            <Route path="/puzzle/:id" element={<PuzzlePage currentUser={currentUser} />} />
            <Route path="/builder" element={<CrosswordBuilder />} />
            <Route path="/archive" element={<Archive />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/leaderboard/:puzzleId" element={<Leaderboard />} />
            <Route path="/stats" element={<UserStats currentUser={currentUser} />} />
          </Routes>
        </MainContent>

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onAuthSuccess={handleAuthSuccess}
        />
      </AppContainer>
    </Router>
  );
}

export default App;
